import { buildQuery } from '@/insights/common/queryUtils'
import { resolveApiPath } from '@/providers/apiRoutes'
import { ApiService, InsightParametersType } from '@/types/dashboard-types'

const getBaseUrl = () =>
  `${window.VITE_MEGALODON_SERVER_URL || import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3000'}`

/**
 * Requests a short-lived download token for this exact URL, then follows the link.
 *
 * We cannot put a bearer header on a plain link, so the server issues a token scoped to the
 * canonicalized URL and `authMiddleware` accepts it in place of the header. The query string used
 * to mint the token and the one the link requests must therefore be byte-identical.
 */
const startDownload = async (api: ApiService, path: string, query: string): Promise<void> => {
  const baseUrl = getBaseUrl()
  // Resolve the whole URL, not just the path: the data source rides in the query string and has to
  // be read and stripped here too, or the CSV would come from a different back end than the chart.
  // Resolving before signing also matters because the token is minted for this exact URL, so the
  // signed and fetched strings have to match.
  const url = resolveApiPath(`${path}?${query}`)
  const downloadPath = `${baseUrl}/auth/downloadToken?url=${encodeURIComponent(url)}`

  // Get a limited-use token to download the URL since we can send our bearer auth thing on a link
  const downloadToken = await api.simpleGet<string>({ path: downloadPath, origin: 'downloadCsv' })

  if (downloadToken) {
    // Server sets Content-Disposition: attachment, so the browser will download
    // rather than navigate. We use a temporary <a> to initiate the request.
    const link = document.createElement('a')
    link.href = `${baseUrl}${url}&downloadToken=${downloadToken}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

/**
 * Downloads a CSV built from `URLSearchParams`, so repeated keys survive.
 *
 * An endpoint whose multi-value filter expects `?companyId=1&companyId=2` cannot read the
 * comma-joined `?companyId=1,2` that `downloadCsv`'s object serialisation produces.
 */
export const downloadCsvWithParams = async (
  api: ApiService,
  path: string,
  params: URLSearchParams,
  title: string
): Promise<void> => {
  const search = new URLSearchParams(params)
  search.set('format', 'csv')
  search.set('title', title)
  await startDownload(api, path, search.toString())
}

/**
 * Downloads a CSV file from the server
 * @param api - The API service instance
 * @param path - The API endpoint path
 * @param parameters - Query parameters to send
 * @param title - The title for the CSV file
 */
export const downloadCsv = async (
  api: ApiService,
  path: string,
  parameters: InsightParametersType,
  title: string
): Promise<void> => {
  try {
    await startDownload(api, path, buildQuery({ ...parameters, format: 'csv', title }))
  } catch (error) {
    console.error('CSV download failed:', error)
    throw error
  }
}
